import { STAFF_SECTIONS, staffName, staffInitials } from "@/lib/staffFields";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Pencil, FileText } from "lucide-react";

export default function StaffDetailModal({ open, onOpenChange, item, onEdit, apiBase = "/staff", extraSections = [] }) {
  if (!item) return null;

  const fmt = (k) => {
    if (k === "doc_synology") return item.doc_synology ? "Oui" : "Non";
    if (k === "caces_doc_synology") return item.caces_doc_synology ? "Oui" : "Non";
    return item[k] || "";
  };

  const viewFile = async (path, name) => {
    try {
      const res = await api.get(`${apiBase}/file/${path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch {
      // eslint-disable-next-line no-alert
    }
  };

  const docs = [
    ["carte_vitale_path", "carte_vitale_name", "Carte vitale (recto)"],
    ["rib_path", "rib_name", "RIB"],
    ["attestation_vm_path", "attestation_vm_name", "Attestation visite médicale"],
  ].filter(([p]) => item[p]);

  const Row = ({ label, value }) => (
    <div className="flex flex-col gap-0.5 py-1.5 border-b border-border/50 last:border-0">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground break-words">{value?.trim() ? value : <span className="text-muted-foreground/60">—</span>}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="staff-detail-modal">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full flex items-center justify-center font-display font-bold text-black text-lg shrink-0"
              style={{ background: item.color || "#F59E0B" }}>
              {staffInitials(item)}
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="font-display text-xl">{staffName(item)}</DialogTitle>
              <p className="text-sm text-muted-foreground">{item.profession || "—"}{item.ville ? ` · ${item.ville}` : ""}</p>
            </div>
            <Button size="sm" variant="outline" onClick={onEdit} data-testid="staff-detail-edit-button">
              <Pencil className="h-4 w-4 mr-1.5" /> Modifier
            </Button>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mt-2">
          {[...STAFF_SECTIONS, ...extraSections].map((section) => (
            <div key={section.title}>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-primary mb-2">{section.title}</h4>
              <div>
                {section.fields.map(([k, label]) => <Row key={k} label={label} value={fmt(k)} />)}
              </div>
            </div>
          ))}

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-primary mb-2">Visite médicale (suivi)</h4>
            <Row label="En attente d'un rendez-vous" value={item.vm_en_attente ? "Oui" : "Non"} />
          </div>

          {docs.length > 0 && (
            <div className="md:col-span-2">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-primary mb-2">Documents</h4>
              <div className="flex flex-wrap gap-2">
                {docs.map(([p, n, label]) => (
                  <Button key={p} size="sm" variant="outline" onClick={() => viewFile(item[p], item[n])}
                    data-testid={`staff-doc-${p}`}>
                    <FileText className="h-4 w-4 mr-1.5" /> {label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {item.notes?.trim() && (
            <div className="md:col-span-2">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-primary mb-2">Notes</h4>
              <p className="text-sm text-foreground whitespace-pre-wrap">{item.notes}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
